import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { persistor } from "../store/store";
import { clearUserInfo } from "../store/authSlice";
import { clearCounts } from "../store/apiCountsSlice";
import { clearCart } from "../store/cartSlice";
import { clearWishlist } from "../store/wishlistSlice";
import {
  clearAuthCookies,
  readAuthFromCookies,
  deleteUserId,
  deleteGuestId,
} from "../utilities/authUtils";

export interface UseNavAuthReturn {
  isLoggedIn: boolean;
  username: string | null;
  cartCount: number;
  wishlistCount: number;
  logout: () => Promise<void>;
}

export const useNavAuth = (): UseNavAuthReturn => {
  const dispatch = useDispatch();
  const [cookieUsername, setCookieUsername] = useState<string | null>(null);

  const { auth, apiCounts } = useSelector(
    (s: any) => ({ auth: s.auth, apiCounts: s.apiCounts }),
    shallowEqual
  );

  // Fallback: read username from cookies when Redux is empty
  useEffect(() => {
    if (!auth?.userInfo) {
      const c = readAuthFromCookies();
      setCookieUsername(c.username || null);
    }
  }, [auth?.userInfo]);

  const logout = useCallback(async () => {
    // 1) Clear Redux slices
    dispatch(clearUserInfo());
    dispatch(clearCart());
    dispatch(clearWishlist());
    dispatch(clearCounts());

    // 2) Clear auth cookies and stored IDs
    clearAuthCookies();
    deleteUserId();
    deleteGuestId();

    // 3) Purge Redux Persist
    try {
      await persistor.purge();
    } catch (err) {
      console.warn("Failed to purge persistor", err);
    }

    // 4) Clear all cookies
    try {
      document.cookie.split(";").forEach((c) => {
        const name = c.split("=")[0].trim();
        if (name) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;SameSite=Lax`;
        }
      });
    } catch (err) {
      console.warn("Failed to clear cookies", err);
    }

    // 5) Clear local/session storage
    try {
      localStorage.removeItem("persist:root");
      localStorage.clear();
      sessionStorage.clear();
    } catch (err) {
      console.warn("Failed to clear storage", err);
    }

    // Reload so interceptors pick up cleared tokens
    window.location.href = "/";
  }, [dispatch]);

  const isLoggedIn = !!(auth?.userInfo || cookieUsername);
  const username: string | null = auth?.userInfo?.username || cookieUsername || null;
  const cartCount: number = apiCounts?.cartCount ?? 0;
  const wishlistCount: number = apiCounts?.wishlistCount ?? 0;

  return { isLoggedIn, username, cartCount, wishlistCount, logout };
};
